import { useEffect, useRef, useState, type CSSProperties } from 'react'

/**
 * PapersBanner — a permanent (non-dismissible) promo banner inviting visitors to
 * try Papers AI. Copy and height are now FIXED per site (the winners of the prior
 * copy/size test). The live A/B test is on banner POSITION: each visitor is stuck
 * to either `static` (scrolls away with the page, today's behaviour) or `sticky`
 * (pinned to the top, follows the viewport). The CTA link carries
 * `ref=<siteRef>&var=<variant id>&size=<size id>&pos=<static|sticky>` so clicks can
 * be attributed to the position arm.
 *
 * The component stays framework- and Tailwind-agnostic: all of ITS OWN layout lives
 * in a scoped <style> block driven by CSS custom properties, and the dot-wave is
 * drawn on a <canvas>. It advertises its chosen arm and live height to the document
 * root (`data-pai-pos` and `--pai-sticky-h` on <html>) so each host can pin the
 * banner and offset its own sticky nav/header without the banner needing to know the
 * host layout. Sizes itself with container queries and reacts to a `.dark` ancestor.
 */

export interface PapersBannerVariant {
  /** Stable analytics id, sent as `var=` on the CTA link (e.g. 'cta_next_step'). */
  id: string
  headline: string
  bullets: string[]
  ctaLabel: string
}

export interface PapersBannerSize {
  /** Stable analytics id, sent as `size=` on the CTA link (e.g. 'standard', 'tall'). */
  id: string
  /** Min height of the content row. */
  minHeight: string
  /** Vertical padding of the content row. */
  padY: string
}

/** Position A/B arms. `static` is today's behaviour; `sticky` pins to the top. */
type PapersBannerPosition = 'static' | 'sticky'

export interface PapersBannerProps {
  /** The single fixed copy variant (winner of the prior copy test). */
  variant: PapersBannerVariant
  /** The single fixed height (winner of the prior size test). */
  size: PapersBannerSize
  /** Source site id, sent as `ref=` on the CTA link ('writefull_com' | 'x_writefull_com'). */
  siteRef: string
  /** Base CTA destination; `ref`, `var`, `size` and `pos` params are appended. */
  ctaHref?: string
  /** Force a palette. Default 'auto' follows a `.dark` ancestor on <html>. */
  theme?: 'auto' | 'light' | 'dark'
  /** localStorage key for the sticky position assignment (default derived from siteRef). */
  storageKey?: string
}

type RGB = [number, number, number]

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}
const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
]

// Uniform, sticky-per-browser assignment of an index in [0, n).
const pickSticky = (key: string, n: number): number => {
  let idx = Math.floor(Math.random() * n)
  try {
    const stored = window.localStorage.getItem(key)
    const parsed = stored === null ? NaN : parseInt(stored, 10)
    if (Number.isInteger(parsed) && parsed >= 0 && parsed < n) {
      idx = parsed
    } else {
      window.localStorage.setItem(key, String(idx))
    }
  } catch {
    // localStorage unavailable (private mode); keep the random pick.
  }
  return idx
}

const buildHref = (
  base: string,
  siteRef: string,
  varId: string,
  sizeId: string,
  posId: string,
) => {
  const sep = base.includes('?') ? '&' : '?'
  return `${base}${sep}ref=${encodeURIComponent(siteRef)}&var=${encodeURIComponent(varId)}&size=${encodeURIComponent(sizeId)}&pos=${encodeURIComponent(posId)}`
}

interface Palette {
  bg: string
  bg2: string
  border: string
  ink: string
  coral: string
  ctaBg: string
  ctaBgHover: string
  ctaInk: string
  focus: string
  dotBase: RGB
  dotYellow: RGB
  dotFloor: number
  dotGain: number
}

const LIGHT: Palette = {
  bg: '#C9DBEA',
  bg2: '#D6E4F0',
  border: 'rgba(6,12,26,0.06)',
  ink: '#0B1526',
  coral: '#EC5F52',
  ctaBg: '#060C1A',
  ctaBgHover: '#1B2740',
  ctaInk: '#FAF9F7',
  focus: '#0B1526',
  dotBase: [244, 248, 251],
  dotYellow: [242, 233, 106],
  dotFloor: 0.5,
  dotGain: 0.5,
}

const DARK: Palette = {
  bg: '#0F1E33',
  bg2: '#17304F',
  border: 'rgba(255,255,255,0.08)',
  ink: '#EAF1F8',
  coral: '#FF7A6D',
  ctaBg: '#F4F8FB',
  ctaBgHover: '#FFFFFF',
  ctaInk: '#0B1526',
  focus: '#FFFFFF',
  dotBase: [150, 178, 214],
  dotYellow: [242, 233, 106],
  dotFloor: 0.18,
  dotGain: 0.62,
}

const CSS = `
.pai-root{
  --pai-radius:20px;
  position:relative;
  container-type:inline-size;
  box-sizing:border-box; width:100%;
  padding:16px 16px 0;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
}
.pai-root *{box-sizing:border-box;}

.pai-shell{
  display:grid; grid-template-rows:0fr; opacity:0;
  transition:grid-template-rows .5s cubic-bezier(.22,1,.36,1),opacity .45s ease;
}
.pai-root.pai-entered .pai-shell{grid-template-rows:1fr; opacity:1;}

.pai-card{
  min-height:0; overflow:hidden;
  position:relative;
  border-radius:var(--pai-radius);
  background:linear-gradient(105deg,var(--pai-bg) 0%,var(--pai-bg2) 100%);
  box-shadow:0 1px 2px rgba(6,12,26,.06),0 10px 30px -12px rgba(6,12,26,.22);
  border:1px solid var(--pai-border);
}
.pai-canvas{position:absolute; inset:0; width:100%; height:100%; pointer-events:none; z-index:0;}
.pai-scrim{
  position:absolute; inset:0; z-index:0; pointer-events:none;
  background:linear-gradient(90deg,var(--pai-bg) 0%,var(--pai-bg) 26%,transparent 60%);
}

.pai-content{
  position:relative; z-index:1;
  display:flex; align-items:center; flex-wrap:wrap; gap:14px 28px;
  padding:var(--pai-pad-y,20px) var(--pai-pad-x,26px);
  min-height:var(--pai-minh,96px);
}
.pai-headline{
  margin:0; flex:0 0 auto; max-width:340px;
  font-family:"Tiempos Text","Times New Roman",Georgia,serif;
  font-weight:600; font-size:28px; line-height:1.12; letter-spacing:-.01em;
  color:var(--pai-ink);
}
.pai-bullets{
  margin:0; padding:0; list-style:none;
  flex:0 1 auto; min-width:0;
  display:grid; gap:9px; align-content:center;
}
.pai-bullets li{
  display:flex; align-items:center; gap:11px;
  font-size:16.5px; line-height:1.25; font-weight:500;
  color:var(--pai-ink);
  white-space:nowrap;
}
.pai-dot{
  flex:0 0 auto; width:8px; height:8px; border-radius:50%;
  background:var(--pai-coral);
  box-shadow:0 0 0 3px rgba(236,95,82,.2);
  box-shadow:0 0 0 3px color-mix(in srgb,var(--pai-coral) 20%,transparent);
}
.pai-cta{
  flex:0 0 auto; align-self:center; margin-left:auto;
  display:inline-flex; align-items:center; justify-content:center;
  padding:14px 26px; border-radius:12px;
  background:var(--pai-cta-bg); color:var(--pai-cta-ink);
  font-size:16px; font-weight:700; text-decoration:none; white-space:nowrap;
  transition:background .18s ease,transform .18s ease,box-shadow .18s ease;
  box-shadow:0 6px 16px -8px rgba(6,12,26,.5);
}
.pai-cta:hover{background:var(--pai-cta-bg-hover); transform:translateY(-1px);}
.pai-cta:active{transform:translateY(0);}
.pai-cta:focus-visible{outline:2.5px solid var(--pai-focus); outline-offset:3px;}

@container (max-width:1080px){
  .pai-headline{font-size:24px; max-width:280px;}
  .pai-content{gap:14px 20px; --pai-pad-x:22px;}
  .pai-bullets li{font-size:15px;}
}
@container (max-width:820px){
  .pai-canvas{opacity:.5;}
  .pai-scrim{background:linear-gradient(90deg,var(--pai-bg) 0%,var(--pai-bg) 20%,transparent 72%);}
  .pai-content{gap:14px 20px; --pai-pad-x:20px;}
  .pai-headline{flex:1 1 100%; max-width:none; font-size:24px;}
  .pai-bullets{flex:1 1 100%;}
  .pai-bullets li{white-space:normal;}
  .pai-cta{flex:1 1 100%; margin-left:0; padding:15px 26px;}
}
@container (max-width:520px){
  .pai-root{padding:10px 12px 0;}
  .pai-headline{font-size:22px;}
  .pai-bullets li{font-size:14.5px;}
}

@media (prefers-reduced-motion: reduce){
  .pai-shell{transition:opacity .2s ease;}
  .pai-cta{transition:background .18s ease;}
  .pai-cta:hover{transform:none;}
}
`

export default function PapersBanner({
  variant,
  size,
  siteRef,
  ctaHref = 'https://papers.ai/',
  theme = 'auto',
  storageKey = undefined,
}: PapersBannerProps) {
  const [mounted, setMounted] = useState(false)
  const [entered, setEntered] = useState(false)
  const [position, setPosition] = useState<PapersBannerPosition | null>(null)
  const [isDark, setIsDark] = useState(theme === 'dark')

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Mount + assign the position arm (client-only, avoids SSR mismatch).
  // A `?pai_pos=static|sticky` query param forces a specific arm for that page view
  // — for sharing a preview with stakeholders — WITHOUT persisting it, so the
  // viewer's own A/B bucket is left untouched. Otherwise each visitor gets a
  // uniform, sticky-per-browser assignment.
  useEffect(() => {
    setMounted(true)
    let forced: PapersBannerPosition | null = null
    try {
      const raw = new URLSearchParams(window.location.search).get('pai_pos')
      if (raw === 'static' || raw === 'sticky') {
        forced = raw
      }
    } catch {
      // window.location unavailable; fall back to the random assignment.
    }
    const idx = pickSticky(storageKey ?? `papersai-pos-${siteRef}`, 2)
    setPosition(forced ?? (idx === 1 ? 'sticky' : 'static'))
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [siteRef, storageKey])

  // Follow a `.dark` ancestor when theme === 'auto'.
  useEffect(() => {
    if (theme !== 'auto') {
      setIsDark(theme === 'dark')
      return undefined
    }
    const root = document.documentElement
    const read = () => setIsDark(root.classList.contains('dark'))
    read()
    const mo = new MutationObserver(read)
    mo.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => mo.disconnect()
  }, [theme])

  // Advertise the chosen arm and the banner's live height to the document root so
  // each host can pin the banner (`html[data-pai-pos="sticky"]`) and offset its own
  // sticky nav/header by `var(--pai-sticky-h)`. The height tracks the entrance
  // animation, so a host nav slides down in step with the expanding banner.
  useEffect(() => {
    if (position === null) {
      return undefined
    }
    const root = rootRef.current
    if (!root) {
      return undefined
    }
    const el = document.documentElement
    el.dataset.paiPos = position
    const publishHeight = () => {
      el.style.setProperty('--pai-sticky-h', `${Math.round(root.getBoundingClientRect().height)}px`)
    }
    publishHeight()
    const ro = new ResizeObserver(publishHeight)
    ro.observe(root)
    return () => {
      ro.disconnect()
      delete el.dataset.paiPos
      el.style.removeProperty('--pai-sticky-h')
    }
  }, [position])

  const palette = isDark ? DARK : LIGHT

  // The dot-wave animation.
  useEffect(() => {
    if (position === null) {
      return undefined
    }
    const canvas = canvasRef.current
    const card = cardRef.current
    if (!canvas || !card) {
      return undefined
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return undefined
    }

    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const SPACING = 18
    const baseR = 1.6
    const grow = 3.4
    const dotBase = palette.dotBase
    const dotYellow = palette.dotYellow
    const floor = palette.dotFloor
    const gain = palette.dotGain

    // Wave parameters. A plane wave whose vertical wavefront is perturbed by a
    // y-dependent sine, so the bright crests read as *wavy vertical stripes* that
    // drift rightward across the field — the reference banner's signature.
    const KX = 0.05 // stripe frequency (spacing ~ 2*pi/KX px)
    const KY = 0.03 // vertical undulation frequency
    const AMP = 30 // horizontal wobble amplitude (px)
    const W1 = 1.9 // travel speed
    const W2 = 1.3 // wobble speed
    const SHARP = 2.6 // crest sharpness (higher = narrower, brighter stripes)

    let raf = 0
    let W = 0
    let H = 0

    const draw = (tms: number) => {
      const t = reduce ? 1.4 : tms * 0.001
      ctx.clearRect(0, 0, W, H)
      // Dots fill from the middle to the right edge (including behind the CTA),
      // fading out to the left; a scrim keeps them clear of the text.
      for (let y = SPACING * 0.7; y < H; y += SPACING) {
        const wobble = AMP * Math.sin(y * KY + t * W2)
        for (let x = SPACING * 0.5; x < W; x += SPACING) {
          const nx = x / W
          const fade = smoothstep(0.36, 0.66, nx) // 0 left -> 1 right
          if (fade <= 0.001) {
            continue
          }

          const phase = (x - wobble) * KX - t * W1
          const c = Math.cos(phase)
          const crest = Math.max(0, c)
          const activation = Math.pow(crest, SHARP) // narrow, bright stripes

          const r = (baseR + activation * grow) * (0.55 + 0.95 * fade)
          const [cr, cg, cb] = mix(dotBase, dotYellow, activation)
          const alpha = fade * (floor + activation * gain)
          if (alpha <= 0.004) {
            continue
          }

          const dy = reduce ? 0 : Math.sin(phase) * 1.7 * activation

          ctx.beginPath()
          ctx.fillStyle = `rgba(${cr | 0},${cg | 0},${cb | 0},${alpha.toFixed(3)})`
          ctx.arc(x, y + dy, r, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      if (!reduce) {
        raf = requestAnimationFrame(draw)
      }
    }

    const resize = () => {
      const rect = card.getBoundingClientRect()
      W = Math.max(1, Math.round(rect.width))
      H = Math.max(1, Math.round(rect.height))
      const dpr = Math.min(2, window.devicePixelRatio || 1)
      canvas.width = Math.round(W * dpr)
      canvas.height = Math.round(H * dpr)
      canvas.style.width = W + 'px'
      canvas.style.height = H + 'px'
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      // Resizing the bitmap wipes it; in reduced-motion mode nothing loops, so
      // repaint the single static frame here.
      if (reduce) {
        draw(0)
      }
    }

    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(card)
    raf = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [position, isDark, palette])

  if (!mounted || position === null) {
    return null
  }

  const href = buildHref(ctaHref, siteRef, variant.id, size.id, position)

  const cssVars: Record<string, string> = {
    '--pai-bg': palette.bg,
    '--pai-bg2': palette.bg2,
    '--pai-border': palette.border,
    '--pai-ink': palette.ink,
    '--pai-coral': palette.coral,
    '--pai-cta-bg': palette.ctaBg,
    '--pai-cta-bg-hover': palette.ctaBgHover,
    '--pai-cta-ink': palette.ctaInk,
    '--pai-focus': palette.focus,
    '--pai-minh': size.minHeight,
    '--pai-pad-y': size.padY,
  }

  return (
    <div
      ref={rootRef}
      className={`pai-root${entered ? ' pai-entered' : ''}`}
      style={cssVars as CSSProperties}
      role='region'
      aria-label='Papers AI announcement'
      data-pai-var={variant.id}
      data-pai-size={size.id}
      data-pai-pos={position}
    >
      <style>{CSS}</style>
      <div className='pai-shell'>
        <div className='pai-card' ref={cardRef}>
          <canvas className='pai-canvas' ref={canvasRef} aria-hidden='true' />
          <div className='pai-scrim' aria-hidden='true' />
          <div className='pai-content'>
            <p className='pai-headline'>{variant.headline}</p>
            <ul className='pai-bullets'>
              {variant.bullets.map((b, i) => (
                <li key={i}>
                  <span className='pai-dot' aria-hidden='true' />
                  {b}
                </li>
              ))}
            </ul>
            <a
              className='pai-cta'
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={`${variant.ctaLabel} — Papers AI (opens in a new tab)`}
            >
              {variant.ctaLabel}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
